import type {
  JsonObject,
  RuntimeCommand,
  RuntimeErrorInfo,
  RuntimeSessionSnapshot,
  RuntimeStatus,
} from '../contracts/index.ts';
import { runtimeCommandSchema } from '../contracts/index.ts';

export type {
  RuntimeErrorInfo,
  RuntimeSessionSnapshot,
  RuntimeStatus,
} from '../contracts/index.ts';

export type RuntimeSessionListener = (
  snapshot: RuntimeSessionSnapshot,
) => void;

export interface RuntimeSession {
  readonly id: string;
  readonly capabilityKey: string;
  start: () => void | Promise<void>;
  pause: () => void | Promise<void>;
  resume: () => void | Promise<void>;
  stop: () => void | Promise<void>;
  reset: () => void | Promise<void>;
  step: (steps?: number) => void | Promise<void>;
  setSpeed: (speed: number) => void;
  updateParameters: (parameters: JsonObject) => void | Promise<void>;
  dispatch: (command: RuntimeCommand) => void | Promise<void>;
  getSnapshot: () => RuntimeSessionSnapshot;
  subscribe: (listener: RuntimeSessionListener) => () => void;
  dispose: () => void;
}

export abstract class BaseRuntimeSession implements RuntimeSession {
  readonly id: string;
  readonly capabilityKey: string;
  protected status: RuntimeStatus = 'idle';
  protected elapsedTime = 0;
  protected speed = 1;
  protected parameters: JsonObject;
  protected state: JsonObject = {};
  protected error: RuntimeErrorInfo | null = null;
  private readonly listeners = new Set<RuntimeSessionListener>();
  private disposed = false;
  private startPromise: Promise<void> | null = null;
  private recoveryStatus: RuntimeStatus | null = null;

  protected constructor(
    id: string,
    capabilityKey: string,
    parameters: JsonObject = {},
  ) {
    this.id = id;
    this.capabilityKey = capabilityKey;
    this.parameters = structuredClone(parameters);
  }

  async start(): Promise<void> {
    this.assertUsable('start');
    if (this.status === 'running' || this.status === 'starting') {
      await this.startPromise;
      return;
    }
    if (this.status === 'paused') {
      this.resume();
      return;
    }

    this.status = 'starting';
    this.emit();
    this.startPromise = this.runStart();
    await this.startPromise;
  }

  pause(): void {
    this.assertUsable('pause');
    if (this.status !== 'running')
      return;
    try {
      this.onPause();
      this.status = 'paused';
      this.recoveryStatus = null;
      this.error = null;
      this.emit();
    }
    catch (error) {
      this.fail(error, 'pause', true);
      throw error;
    }
  }

  resume(): void {
    this.assertUsable('resume');
    if (this.status !== 'paused')
      return;
    try {
      this.onResume();
      this.status = 'running';
      this.recoveryStatus = null;
      this.error = null;
      this.emit();
    }
    catch (error) {
      this.fail(error, 'resume', true);
      throw error;
    }
  }

  stop(): void {
    this.assertUsable('stop');
    if (this.status === 'stopped')
      return;
    try {
      this.onStop();
      this.status = 'stopped';
      this.recoveryStatus = null;
      this.error = null;
      this.emit();
    }
    catch (error) {
      this.fail(error, 'stop', true);
      throw error;
    }
  }

  async reset(): Promise<void> {
    this.assertUsable('reset');
    try {
      this.onStop();
      this.elapsedTime = 0;
      this.error = null;
      this.state = {};
      await this.onReset();
      this.status = 'idle';
      this.recoveryStatus = null;
      this.emit();
    }
    catch (error) {
      this.fail(error, 'reset', false);
      throw error;
    }
  }

  async step(steps = 1): Promise<void> {
    this.assertUsable('step');
    if (!Number.isInteger(steps) || steps <= 0)
      throw new Error('运行步数必须是正整数');
    if (steps > 10000)
      throw new Error('单次运行步数不能超过 10000');
    try {
      await this.onStep(steps);
      this.recoverFromError();
      this.emit();
    }
    catch (error) {
      this.fail(error, 'step', true);
      throw error;
    }
  }

  setSpeed(speed: number): void {
    this.assertUsable('speed.set');
    if (!Number.isFinite(speed) || speed <= 0)
      throw new Error('运行速度必须大于 0');
    if (speed > 100)
      throw new Error('运行速度不能超过 100');
    const previous = this.speed;
    this.speed = speed;
    try {
      this.onSpeedChange(speed);
      this.emit();
    }
    catch (error) {
      this.speed = previous;
      throw error;
    }
  }

  async updateParameters(parameters: JsonObject): Promise<void> {
    this.assertUsable('parameters.patch');
    const previous = structuredClone(this.parameters);
    const next = { ...this.parameters, ...structuredClone(parameters) };
    try {
      await this.onParametersChange(next);
      this.parameters = next;
      await this.onParametersCommitted(next);
      this.recoverFromError();
      this.emit();
    }
    catch (error) {
      this.parameters = previous;
      this.fail(error, 'parameters.patch', true);
      throw error;
    }
  }

  async dispatch(command: RuntimeCommand): Promise<void> {
    const parsedCommand = runtimeCommandSchema.parse(command);
    if (parsedCommand.type === 'start') {
      await this.start();
    }
    else if (parsedCommand.type === 'pause') {
      await this.pause();
    }
    else if (parsedCommand.type === 'resume') {
      await this.resume();
    }
    else if (parsedCommand.type === 'stop') {
      await this.stop();
    }
    else if (parsedCommand.type === 'reset') {
      await this.reset();
    }
    else if (parsedCommand.type === 'step') {
      await this.step(parsedCommand.steps);
    }
    else if (parsedCommand.type === 'speed.set') {
      this.setSpeed(parsedCommand.speed);
    }
    else if (parsedCommand.type === 'parameters.patch') {
      await this.updateParameters(parsedCommand.parameters);
    }
    else {
      this.assertUsable('interaction.dispatch');
      try {
        await this.onInteraction(
          parsedCommand.eventType,
          parsedCommand.payload,
        );
        this.recoverFromError();
        this.emit();
      }
      catch (error) {
        this.fail(error, 'interaction.dispatch', true);
        throw error;
      }
    }
  }

  getSnapshot(): RuntimeSessionSnapshot {
    return {
      id: this.id,
      capabilityKey: this.capabilityKey,
      status: this.status,
      elapsedTime: this.elapsedTime,
      speed: this.speed,
      parameters: structuredClone(this.parameters),
      state: structuredClone(this.state),
      error: this.error,
      execution: 'main-thread',
    };
  }

  subscribe(listener: RuntimeSessionListener): () => void {
    this.assertUsable('subscribe');
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    if (this.disposed)
      return;
    try {
      this.onStop();
    }
    catch {
      // Resource cleanup remains best-effort during disposal.
    }
    try {
      this.onDispose();
    }
    catch {
      // A failed stop hook must not prevent the final disposal hook.
    }
    this.disposed = true;
    this.status = 'disposed';
    this.emit();
    this.listeners.clear();
  }

  protected abstract onStep(steps: number): void | Promise<void>;

  protected onStart(): void | Promise<void> {}

  protected onPause(): void {}

  protected onResume(): void {}

  protected onStop(): void {}

  protected onReset(): void | Promise<void> {}

  protected onDispose(): void {}

  protected onSpeedChange(_speed: number): void {}

  protected onParametersChange(_parameters: JsonObject): void | Promise<void> {}

  protected onParametersCommitted(_parameters: JsonObject): void | Promise<void> {}

  protected onInteraction(
    _eventType: string,
    _payload: JsonObject,
  ): void | Promise<void> {}

  protected emit(): void {
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) {
      try {
        listener(snapshot);
      }
      catch {
        // A subscriber cannot break the runtime lifecycle.
      }
    }
  }

  protected fail(
    error: unknown,
    operation: string,
    recoverable: boolean,
  ): void {
    const previousStatus = this.status;
    if (recoverable) {
      this.recoveryStatus = operation === 'start'
        ? 'idle'
        : previousStatus === 'running' || previousStatus === 'starting'
          ? 'paused'
          : previousStatus === 'error'
            ? this.recoveryStatus ?? 'idle'
            : previousStatus;
    }
    else {
      this.recoveryStatus = null;
    }
    if (previousStatus === 'running' || previousStatus === 'starting') {
      try {
        this.onStop();
      }
      catch {
        // The original runtime error remains the diagnostic source.
      }
    }
    this.status = 'error';
    this.error = {
      code: error instanceof Error && error.name
        ? error.name
        : 'RuntimeError',
      message: error instanceof Error ? error.message : '运行时发生未知错误',
      operation,
      recoverable,
      occurredAt: new Date().toISOString(),
    };
    this.emit();
  }

  private async runStart(): Promise<void> {
    try {
      await this.onStart();
      this.status = 'running';
      this.recoveryStatus = null;
      this.error = null;
      this.emit();
    }
    catch (error) {
      this.fail(error, 'start', true);
      throw error;
    }
    finally {
      this.startPromise = null;
    }
  }

  private assertUsable(operation: string): void {
    if (this.disposed)
      throw new Error(`运行会话已经释放，无法执行 ${operation}`);
  }

  private recoverFromError(): void {
    if (this.status === 'error') {
      this.status = this.recoveryStatus === 'running'
        ? 'paused'
        : this.recoveryStatus ?? 'idle';
    }
    this.recoveryStatus = null;
    this.error = null;
  }
}

export class EmptyRuntimeSession extends BaseRuntimeSession {
  constructor(id: string, capabilityKey: string) {
    super(id, capabilityKey);
  }

  protected onStep(steps: number): void {
    this.elapsedTime += steps;
  }
}
